import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCopyToClipboard } from 'react-use'

import { API } from '@/api'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { Button, Divider, message, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { Instance, NetGroup } from '@/models/instance'
import { cn } from '@/utils/style'

import { NetworkGroupEditModal } from './NetworkGroupEditModal'

interface InstanceNetworkGroupProps {
  instance: Instance
}

export function InstanceNetworkGroup({ instance }: InstanceNetworkGroupProps) {
  const { t } = useTranslation()
  const { netGroup } = instance || {}
  const [handing, setHanding] = useState(false)
  const [state, copyToClipboard] = useCopyToClipboard()
  const [modifyNetwork, setModifyNetwork] = useState<NetGroup>()

  const [network, setNetwork] = useState<NetGroup | undefined>(netGroup)

  const navigate = useNavigate()

  useEffect(() => {
    if (state.value) {
      message.success('Copied')
    }
  }, [state])

  const onDelete = async () => {
    setHanding(true)
    try {
      const res = await API.deleteEndpointFromMesh({
        res_id: netGroup!.edpResID,
      })
      if (res.code === 0) {
        message.show('success')
        setNetwork(undefined)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setHanding(false)
    }
  }

  return (
    <div className="bg-panel-gray-100 rounded-lg">
      {network ? (
        <>
          <div className="flex flex-wrap py-4 gap-y-4">
            <div key={'ID'} className="w-1/4 px-4">
              <div className="t-xs text-secondary">
                <Trans i18nKey="instance.detail.networkGroup.tableHeaderID">ID</Trans>
              </div>
              <div
                className="flex items-center gap-1 cursor-pointer t-sb text-primary"
                onClick={() => {
                  copyToClipboard(network.netResID)
                }}
              >
                {network.netResID}
                <CopyIcon className="w-4 h-4 text-btn-secondary" />
              </div>
            </div>

            <div key={'Name'} className="w-1/4 px-4">
              <div className="t-xs text-secondary">
                <Trans i18nKey="instance.detail.networkGroup.tableHeaderName">Name</Trans>
              </div>
              <div className="t-sb text-primary">{network.networkName}</div>
            </div>
            <div key={'IPv4CIDR'} className="w-1/4 px-4">
              <div className="t-xs text-secondary">IPv4 CIDR</div>
              <div
                className="flex items-center gap-1 cursor-pointer t-sb text-primary"
                onClick={() => {
                  copyToClipboard(network.cidr)
                }}
              >
                {network.cidr}
                <CopyIcon className="w-4 h-4 text-btn-secondary" />
              </div>
            </div>
          </div>
          <div>
            <div>
              <div className="px-4 pb-4">
                <Divider className="mb-4" />
                <div className="flex gap-4">
                  <div
                    className={cn(
                      ' text-btn-secondary',
                      handing ? ' opacity-40 cursor-not-allowed' : 'cursor-pointer',
                    )}
                    onClick={() => setModifyNetwork(netGroup)}
                  >
                    <Trans i18nKey="instance.detail.networkGroup.actionEdit">Edit</Trans>
                  </div>
                  <div
                    className={cn(
                      ' text-red-500',
                      handing ? ' opacity-40 cursor-not-allowed' : 'cursor-pointer',
                    )}
                    onClick={() => {
                      onDelete()
                    }}
                  >
                    <Trans i18nKey="instance.detail.networkGroup.actionDelete">Delete</Trans>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <EmptyView
          content={t('instance.net.emptyViewContent')}
          action={
            <Button type="secondary" size="sm" onClick={() => navigate('/mesh')}>
              <Trans i18nKey="instance.net.emptyViewActionBtn">Go to Mesh Clustering</Trans>
            </Button>
          }
        />
      )}

      {modifyNetwork ? (
        <NetworkGroupEditModal
          onSuccess={(net) => {
            setModifyNetwork(undefined)
            setNetwork(net)
          }}
          onClose={() => {
            setModifyNetwork(undefined)
          }}
          network={modifyNetwork}
        />
      ) : null}
    </div>
  )
}
